import { matchesSearchQuery } from './searchUtils';

const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const EMPTY_PLATE_LETTERS = ['', '', ''];

export const normalizePlateDigits = (value) => (
    String(value || '')
        .normalize('NFKC')
        .replace(/[٠-٩]/g, (digit) => ARABIC_DIGITS.indexOf(digit))
        .replace(/[۰-۹]/g, (digit) => PERSIAN_DIGITS.indexOf(digit))
        .replace(/\D/g, '')
);

export const normalizePlateLetter = (value) => (
    String(value || '')
        .normalize('NFKC')
        .replace(/[\u064B-\u065F\u0670\u0640]/g, '')
        .replace(/[أإآٱ]/g, 'ا')
        .replace(/ى/g, 'ي')
        .replace(/ة/g, 'ه')
        .match(/[\u0600-\u06FF]/)?.[0] || ''
);

export const normalizePlateLetters = (letters = EMPTY_PLATE_LETTERS) => {
    const sourceLetters = Array.isArray(letters)
        ? letters
        : String(letters || '').split('');

    return EMPTY_PLATE_LETTERS.map((_, index) => normalizePlateLetter(sourceLetters[index]));
};

export const parsePlateSearchInput = (value) => {
    const text = String(value || '')
        .normalize('NFKC')
        .replace(/[٠-٩]/g, (digit) => ARABIC_DIGITS.indexOf(digit))
        .replace(/[۰-۹]/g, (digit) => PERSIAN_DIGITS.indexOf(digit))
        .replace(/[\u064B-\u065F\u0670\u0640]/g, '')
        .replace(/[أإآٱ]/g, 'ا')
        .replace(/ى/g, 'ي')
        .replace(/ة/g, 'ه');

    const plateNumber = (text.match(/[0-9]+/g) || []).join('').slice(0, 4);
    const plateLetters = (text.match(/[\u0600-\u06FF]/g) || [])
        .slice(0, 3);

    return {
        plateNumber,
        plateLetters: normalizePlateLetters(plateLetters),
    };
};

export const validatePlateSearch = ({ plateNumber, plateLetters }) => {
    const normalizedNumber = normalizePlateDigits(plateNumber);
    const normalizedLetters = normalizePlateLetters(plateLetters);
    const enteredLetters = normalizedLetters.filter(Boolean);

    return {
        isValidNumber: normalizedNumber.length >= 3 && normalizedNumber.length <= 4,
        isValidLetters: enteredLetters.length >= 2 && enteredLetters.length <= 3,
        plateNumber: normalizedNumber,
        plateLetters: normalizedLetters,
    };
};

export const formatPlateLettersForLookup = (letters = EMPTY_PLATE_LETTERS) => (
    normalizePlateLetters(letters)
        .filter(Boolean)
        .map((letter) => (letter === 'ا' ? 'أ' : letter))
        .join(' ')
);

export const buildPlateLookupValue = ({ plateNumber, plateLetters }) => {
    const normalizedNumber = normalizePlateDigits(plateNumber);
    const formattedLetters = formatPlateLettersForLookup(plateLetters);
    return formattedLetters && normalizedNumber ? `${formattedLetters} ${normalizedNumber}` : '';
};

export const buildPlateLookupCandidates = ({ plateNumber, plateLetters }) => {
    const primaryLookup = buildPlateLookupValue({ plateNumber, plateLetters });
    const reversedLookup = buildPlateLookupValue({
        plateNumber,
        plateLetters: normalizePlateLetters(plateLetters).filter(Boolean).reverse(),
    });

    return [...new Set([primaryLookup, reversedLookup].filter(Boolean))];
};

export const hasPlateSearchInput = ({ plateNumber, plateLetters }) => (
    Boolean(normalizePlateDigits(plateNumber))
    || normalizePlateLetters(plateLetters).some(Boolean)
);

export const buildPlateSearchQuery = ({ plateNumber, plateLetters }) => {
    const normalizedNumber = normalizePlateDigits(plateNumber);
    const normalizedLetters = normalizePlateLetters(plateLetters).filter(Boolean).join(' ');
    return [normalizedNumber, normalizedLetters].filter(Boolean).join(' ');
};

const getVehicleSearchValues = (vehicle = {}) => [
    vehicle.license_plate,
    vehicle.chassis_number,
    vehicle.vehicle_name,
    vehicle.car_name,
    vehicle.make,
    vehicle.model,
    vehicle.vehicle_status,
    vehicle.status,
];

export const filterVehiclesByPlateSearch = (vehicles = [], searchInput = {}) => {
    const query = buildPlateSearchQuery(searchInput);
    if (!query) return vehicles || [];

    return (vehicles || []).filter((vehicle) => (
        matchesSearchQuery(getVehicleSearchValues(vehicle), query)
    ));
};
